import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  initForm,
  mockField,
  personalFields,
  primaryFields,
  showMock,
} from "./form/config";
import { IFieldSpec } from "./form/types";
import { validate } from "./form/validate";
import { useForm } from "./form/useForm";
import { submitLead } from "./api/submit";
import { ILeadApplicant } from "./api/payload";
import { widgetConfig } from "./config";
import {
  isEmbedded,
  notifyError,
  notifyResize,
  notifySuccess,
} from "./iframe/messaging";
import Field from "./components/Field";

const GENERIC_ERROR = "Something went wrong. Please try again.";

const applicantFromForm = (form: any): ILeadApplicant => ({
  requestedLoan: form.requestedLoan.value,
  reason: form.reason.value,
  ssn: form.ssn.value,
  email: form.email.value,
  phoneNumber: form.phoneNumber.value,
  mockData: form.mockData?.value,
});

const ArrowRight = () => (
  <svg
    className="fts-arrow"
    width="18"
    height="14"
    viewBox="0 0 18 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 7h14M10 2l5 5-5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const App = () => {
  const { form, setForm, onChange } = useForm(initForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  // Keep the embedding iframe sized to the widget's content.
  useEffect(() => {
    if (!isEmbedded() || !rootRef.current) return;
    const el = rootRef.current;
    const report = () => notifyResize(el.offsetHeight);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const renderField = (spec: IFieldSpec) => (
    <Field
      key={spec.name}
      spec={spec}
      value={form[spec.name].value}
      message={form[spec.name].message}
      onChange={onChange}
    />
  );

  const openDirectMail = () => {
    if (widgetConfig.directMailUrl) {
      window.open(widgetConfig.directMailUrl, "_blank", "noopener,noreferrer");
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const [isValid, updated] = validate(form);
    if (!isValid) {
      setForm(updated);
      return;
    }

    setLoading(true);
    setError("");
    const { data, error: submitError } = await submitLead(
      applicantFromForm(form),
    );
    setLoading(false);

    if (submitError || !data?.link) {
      const message =
        typeof submitError === "string" ? submitError : GENERIC_ERROR;
      setError(message);
      notifyError(message);
      return;
    }

    if (isEmbedded()) {
      notifySuccess(data.link);
    } else {
      window.location.assign(data.link);
    }
  };

  return (
    <div className="fts-card" ref={rootRef}>
      <h1 className="fts-title">Start Application</h1>

      <form className="fts-form" onSubmit={onSubmit} noValidate>
        <div className="fts-fields fts-fields-primary">
          {primaryFields.map(renderField)}
        </div>

        <div className="fts-fields fts-fields-personal">
          {personalFields.map(renderField)}
        </div>

        {showMock() && (
          <div className="fts-fields fts-fields-mock">
            {[mockField].map(renderField)}
          </div>
        )}

        {error && (
          <div className="fts-form-error" role="alert">
            {error}
          </div>
        )}

        <div className="fts-actions">
          <button className="fts-btn" type="submit" disabled={loading}>
            <span>{loading ? "Submitting…" : "Get Started"}</span>
            {!loading && <ArrowRight />}
          </button>

          <button
            className="fts-btn fts-btn-secondary"
            type="button"
            onClick={openDirectMail}
          >
            Pre-approved mail recipients click HERE
          </button>
        </div>
      </form>
    </div>
  );
};

export default App;
